import { isUsingNewBridgeAPI, isUsingNewElectron } from '@launcher/utils/desktop';
import electronService from '@services/electron/electron.service';
import { i18n } from '@services/i18n';
import { version as appVersion } from '../../../../package.json';

const TRAY_ICON_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAFMSURBVHgBpVPBUcNADJQuecAvHSR0YCrACQ1ABeAZME9MB1BBnB+GB+4AVwBXglNB3AF+5cEMJ9ZnY5MExwR25kZnydrTaU8s/uSNmAb0N+T9Olkoxz6HHbQSlv8k5QePSMTpfwvPOHq5FX8cI3i2lpkRi8eR1uucfeqEpLQvYw51LheuS6Qc6tGQDM2pZ3QHgT351CZfTaZwBHVIFWGVqdrBNLQnsBo2+Tzje51tJDcYMYJC7ciwkpZkC6hgDja8H1BBqXPsrrclf92kxB7lRbnWPuoUigRkzKGVbiuBqIVdS1WetOQQ11qIf3xSEBEZ73cVrGKE7j/Lpevwg05QRbIrQRXtPVn7XlSB97AzAYmDWZlyrHOOXtEPc1MRZbAaAnjdL5EpQE8wH+YOTQ7hCVfCzTvgtGJ3qejBz4gh8dwOHds+HfG/xhkSfwLu3ojG2yMZYAAAAABJRU5ErkJggg==';

class Tray {
  private displayAutoUpdate = false;

  // only for < 100.14.18
  private tray: null | { destroy: () => void } | void;

  private async getIcon() {
    // For compatibility: returns the dataURL directly for new versions
    // and a NativeImage object for old versions
    if (await isUsingNewElectron()) {
      return TRAY_ICON_DATA_URL;
    }
    return window.electron.getNativeImageModule?.().createFromDataURL(TRAY_ICON_DATA_URL);
  }

  public async addUpdater() {
    this.displayAutoUpdate = true;

    // only for < 100.14.18
    if (!isUsingNewBridgeAPI() && !this.tray) {
      return;
    }

    await this.makeTray();
  }

  // only for < 100.14.18
  public remove(): void {
    this.tray?.destroy();
    this.tray = null;
  }

  public async init(): Promise<void> {
    await this.makeTray();

    if (!isUsingNewBridgeAPI()) {
      // only for < 100.14.18
      window.addEventListener('beforeunload', () => this.remove());
    }
  }

  private async getMenuItems() {
    if (!window.electron) return [];
    const localVersion = await electronService.getElectronVersion();

    // click property as action is useless if >= 100.14.18
    // for >= 100.14.18 makeTrayMenu use id to set click as string

    return [
      {
        id: 'dashboard',
        label: i18n.t('bringWindowToFront'),
        click: () => window.electron.showCurrentWindow(),
      },
      {
        id: 'about',
        label: i18n.t('help.about'),
        submenu: [
          {
            label: `${i18n.t('app.distantVersion')} ${appVersion}`,
            enabled: false,
          },
          {
            label: `${i18n.t('app.localVersion')} ${localVersion}`,
            enabled: false,
          },
        ],
      },
      ...(this.displayAutoUpdate ? [this.getMenuItemAutoUpdate()] : []),
      {
        id: 'exit',
        label: i18n.t('exit'),
        click: () => window.electron.exitApp(),
      },
    ] as any;
  }

  private getMenuItemAutoUpdate() {
    return {
      id: 'update',
      label: i18n.t('app.updateAvailable'),
      click: () => {
        window.electron.getAutoUpdater().quitAndInstall();
      },
    };
  }

  private async makeTray() {
    const isNewBridgeAPI = await isUsingNewBridgeAPI();
    const isNewElectron = await isUsingNewElectron();
    const icon = await this.getIcon();

    if (!isNewBridgeAPI && !isNewElectron) {
      // only for < 100.14.18
      this.remove();
      this.tray = window.electron.makeTrayMenu({
        icon,
        menuItems: await this.getMenuItems(),
      }) as void | { destroy: () => void };
    } else {
      window.electron.makeTrayMenu({
        icon,
        menuItems: await this.getMenuItems(),
      }) as void | { destroy: () => void };
    }
  }
}

export const trayMenu = new Tray();
